import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import '../styles/homeCarousel.css';

/**
 * HomeCarousel Component
 * Matches the carousel design from EdibleCupsCategory page
 * Features: Navigation arrows, touch swipe, auto-play, smooth transitions
 */
const HomeCarousel = ({ 
  images, 
  title,
  subtitle,
  autoPlayInterval = 2500,
  showTitle = false 
}) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const carouselRef = useRef(null);
  const autoPlayRef = useRef(null);

  const totalSlides = images.length;

  // Go to next slide
  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % totalSlides);
  }, [totalSlides]);

  // Go to previous slide
  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
  }, [totalSlides]);

  // Go to specific slide
  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  // Auto-play functionality
  useEffect(() => {
    if (!isPaused && totalSlides > 1) {
      autoPlayRef.current = setInterval(() => {
        nextSlide();
      }, autoPlayInterval);
    }

    return () => {
      if (autoPlayRef.current) {
        clearInterval(autoPlayRef.current);
      }
    };
  }, [isPaused, nextSlide, autoPlayInterval, totalSlides]);

  // Touch handlers for mobile swipe
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      nextSlide();
    } else if (isRightSwipe) {
      prevSlide();
    }
  };

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (e.key === 'ArrowLeft') {
      prevSlide();
    } else if (e.key === 'ArrowRight') {
      nextSlide();
    }
  };

  return (
    <div className="home-carousel-section">
      {(title || subtitle) && (
        <div className="home-carousel-header">
          {title && <h3 className="home-carousel-title">{title}</h3>}
          {subtitle && <p className="home-carousel-subtitle">{subtitle}</p>}
        </div>
      )}

      <div 
        className="home-carousel"
        ref={carouselRef}
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        role="region"
        aria-label="Image carousel"
      >
        {/* Previous Button */}
        <button 
          className="home-carousel-btn home-carousel-btn-prev" 
          onClick={prevSlide}
          aria-label="Previous slide"
        >
          <ChevronLeft size={28} />
        </button>

        {/* Slides Container */}
        <div className="home-carousel-slides-container">
          <div 
            className="home-carousel-slides"
            style={{ transform: `translateX(-${currentSlide * 100}%)` }}
          >
            {images.map((image, index) => (
              <div key={index} className="home-carousel-slide">
                <img 
                  src={image.url || image.src} 
                  alt={image.alt || `Slide ${index + 1}`} 
                  loading={index === 0 ? "eager" : "lazy"}
                />
                {showTitle && image.title && (
                  <div className="home-carousel-slide-overlay">
                    <span className="home-carousel-slide-title">{image.title}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Next Button */}
        <button 
          className="home-carousel-btn home-carousel-btn-next" 
          onClick={nextSlide}
          aria-label="Next slide"
        >
          <ChevronRight size={28} />
        </button>

        {/* Dots Indicator */}
        <div className="home-carousel-dots">
          {images.map((_, index) => (
            <button
              key={index}
              className={`home-carousel-dot ${index === currentSlide ? 'active' : ''}`}
              onClick={() => goToSlide(index)}
              aria-label={`Go to slide ${index + 1}`}
              aria-current={index === currentSlide ? 'true' : 'false'}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default HomeCarousel;
